import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData, VerifyData, ConvertToGoodReceipt, ConvertPurchaseVerify } from '../../../apiService';
import apiConfig from '../../../apiConfig';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Add, Edit, Visibility, Delete, ShoppingCart, FilterList, Refresh, Warning, CheckCircle, Cancel, Close, ExpandMore, ExpandLess, CheckCircleOutline, Assignment,
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  WarningIcon as WarningAmberIcon
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Collapse, Divider, Grid,
  Card, CardContent, TextField, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Fade, CircularProgress
} from '@mui/material';
import PurchaseModal from './PurchaseModal';
import VerifyPurchaseModal from './VerifyPurchaseModal';
import Pagination from '../../common/Pagination';
import './PurchaseIndex.css';

const PurchaseIndex = () => {
  // Session validation
  useSessionValidation();

  const history = useHistory();
  const { companyId, branchId, token, userId, permissions } = useAuth();

  const hasCreateMasterPermission = permissions?.includes('Create Master');
  const hasEditMasterPermission = permissions?.includes('Edit Master');
  const hasDeleteMasterPermission = permissions?.includes('Delete Master');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Verify modal states
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyPurchaseData, setVerifyPurchaseData] = useState(null);
  const [verifyPurchaseDetails, setVerifyPurchaseDetails] = useState([]);

  // Data states
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);

  // Inactive modal states
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [inactiveViewData, setInactiveViewData] = useState(null);
  const [inactiveViewDetails, setInactiveViewDetails] = useState([]);

  // Converted PO List modal states
  const [convertedPOModalOpen, setConvertedPOModalOpen] = useState(false);
  const [convertedPOList, setConvertedPOList] = useState([]);
  const [requestCount, setRequestCount] = useState(0);

  // PO to Purchase Create modal states
  const [poToPurchaseModalOpen, setPOToPurchaseModalOpen] = useState(false);
  const [selectedPOData, setSelectedPOData] = useState(null);
  const [selectedPODetails, setSelectedPODetails] = useState([]);
  const [poToPurchaseFormData, setPOToPurchaseFormData] = useState({});

  // PO Details View modal states
  const [poDetailsModalOpen, setPODetailsModalOpen] = useState(false);
  const [poDetailsData, setPODetailsData] = useState(null);
  const [poDetailsItems, setPODetailsItems] = useState([]);

  // Purchase Return modal states
  const [purchaseReturnModalOpen, setPurchaseReturnModalOpen] = useState(false);
  const [purchaseReturnData, setPurchaseReturnData] = useState(null);
  const [purchaseReturnDetails, setPurchaseReturnDetails] = useState([]);
  const [purchaseReturnFormData, setPurchaseReturnFormData] = useState({});

  // Purchase Details View modal states (for bill number click in Purchase Return)
  const [purchaseDetailsViewModalOpen, setPurchaseDetailsViewModalOpen] = useState(false);
  const [purchaseDetailsViewData, setPurchaseDetailsViewData] = useState(null);
  const [purchaseDetailsViewItems, setPurchaseDetailsViewItems] = useState([]);

  // Supplier drill-down modal states
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierPurchases, setSupplierPurchases] = useState([]);
  const [expandedPurchaseId, setExpandedPurchaseId] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Mobile and UI states
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mounted, setMounted] = useState(false);

  // Statistics states
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    verified: 0,
    pending: 0
  });

  // Form states
  const [formData, setFormData] = useState({});
  const [purchaseDetails, setPurchaseDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  // Convert to Good Receipt confirmation modal
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertItemId, setConvertItemId] = useState(null);
  const [convertItemNo, setConvertItemNo] = useState('');

  // Freight + Packing GST state
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  // Watch for changes in selectedPODetails and recalculate totals
  useEffect(() => {
    if (selectedPODetails.length > 0) {
      calculatePOTotals(selectedPODetails);
    }
  }, [selectedPODetails.length]); // Only trigger when items are added/removed

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMounted(true);
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
    }
  }, [companyId, token, fromDate, toDate]);

  // Fetch functions
  const fetchPurchases = async () => {
    try {
      const activeResponse = await fetchData('PurchaseApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));

      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('PurchaseApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive purchases:', inactiveError);
      }

      const allPurchases = [...activeData, ...inactiveData];
      setPurchases(allPurchases);
      calculateStatistics(allPurchases);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setPurchases([]);
      calculateStatistics([]);
    }
  };

  const calculateStatistics = (purchaseData) => {
    if (!Array.isArray(purchaseData)) {
      setStatistics({ total: 0, active: 0, verified: 0, pending: 0 });
      return;
    }

    const stats = {
      total: purchaseData.length,
      active: purchaseData.filter(item => item.IsActive).length,
      verified: purchaseData.filter(item => item.IsVerified).length,
      pending: purchaseData.filter(item => item.IsActive && !item.IsVerified).length
    };
    setStatistics(stats);
  };

  const fetchSuppliers = async () => {

    try {
      const response = await fetchData('VendorApi', `/${companyId}`, token);
      setSuppliers(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('PurchaseApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchFreightAndPackingGST = async () => {
    try {
      const response = await fetchData('PurchaseApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || [];
      let freightData = null, packingData = null;
      if (Array.isArray(data)) {
        freightData = data.find(item => item.Name?.toLowerCase().includes('freight'));
        packingData = data.find(item => item.Name?.toLowerCase().includes('packing'));
      }
      setFreightPackingGST({ freight: freightData, packing: packingData });
      return { freight: freightData, packing: packingData };
    } catch (error) {
      console.error('Error fetching freight and packing GST:', error);
      return { freight: null, packing: null };
    }
  };

  const fetchPurchaseReturnMaxBillNo = async () => {
    try {
      const response = await fetchData('PurchaseReturnApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching purchase return max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Fetching purchases...');
    try {
      await Promise.allSettled([
        fetchPurchases(),
        fetchSuppliers(),
        fetchItems(),
        fetchFreightAndPackingGST(),
        // Load request count on initial load
        fetchConvertedPOList().then(poList => setRequestCount(poList.length))
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Purchase data specifically for Purchase Return
  const fetchPurchaseForReturn = async (purchaseId) => {
    try {
      const response = await fetchData('PurchaseReturnApi', `/PurchaseData/${companyId}/${purchaseId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching purchase data for return:', error);
      return null;
    }
  };

  // Fetch single Purchase details for view modal
  const fetchSinglePurchase = async (purchaseId) => {
    try {
      const response = await fetchData('PurchaseApi', `/${purchaseId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching single purchase:', error);
      return null;
    }
  };

  // Fetch inactive Purchase details for view modal
  const fetchInactivePurchaseDetails = async (id) => {
    try {
      const response = await fetchData('PurchaseApi', `/InActivePurchaseListDetails/${id}/${branchId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching inactive purchase details:', error);
      return null;
    }
  };

  // Fetch Converted PO List
  const fetchConvertedPOList = async () => {
    try {
      const response = await fetchData('PurchaseApi', `/PurchaseOrderToPurchase/${companyId}/${branchId}`, token);
      return response.response_data || response || [];
    } catch (error) {
      console.error('Error fetching converted PO list:', error);
      return [];
    }
  };

  // Handle Request button click - Open Converted PO List modal
  const handleOpenConvertedPOModal = async () => {
    setModalLoading(true);
    const poList = await fetchConvertedPOList();
    setConvertedPOList(poList);
    setRequestCount(poList.length); // Set the count
    setModalLoading(false);
    setConvertedPOModalOpen(true);
  };

  const handleCloseConvertedPOModal = () => {
    setConvertedPOModalOpen(false);
    setConvertedPOList([]);
  };

  // Fetch PO details for PO to Purchase
  const fetchPODetailsForPurchase = async (poId) => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/${poId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching PO details:', error);
      return null;
    }
  };

  // Handle "To Purchase" button click
  const handleOpenPOToPurchaseModal = async (po) => {
    setModalLoading(true);

    const poDetails = await fetchPODetailsForPurchase(po.Id);

    if (poDetails) {
      const poData = poDetails.PurchaseOrder || poDetails;
      const details = poDetails.PurchaseOrderDetails || [];

      setSelectedPOData(poData);

      // Initialize details with proper calculation structure
      const initializedDetails = details.map(d => ({
        ...d,
        Quantity: d.Quantity || 0,
        Amount: (d.Quantity || 0) * (d.Rate || 0),
        DiscountPercentage: d.DiscountPercentage || 0,
        DiscountAmount: d.DiscountAmount || 0,
        TaxableAmount: d.TaxableAmount || ((d.Quantity || 0) * (d.Rate || 0) - (d.DiscountAmount || 0)),
        CGSTAmount: d.CGSTAmount || 0,
        SGSTAmount: d.SGSTAmount || 0,
        IGSTAmount: d.IGSTAmount || 0,
        TotalAmount: d.TotalAmount || ((d.Quantity || 0) * (d.Rate || 0) - (d.DiscountAmount || 0) + (d.CGSTAmount || 0) + (d.SGSTAmount || 0) + (d.IGSTAmount || 0))
      }));

      setSelectedPODetails(initializedDetails);

      // Get GST rates for freight and packing
      const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate) || 0;
      const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate) || 0;
      const maxItemGSTRate = initializedDetails.length > 0 ? Math.max(...initializedDetails.map(d => parseFloat(d.GSTRate) || 0)) : 0;

      const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
      const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);

      // Calculate initial freight and packing GST amounts
      const freightAmount = poData.FreightAmount || 0;
      const packingAmount = poData.PackingAmount || 0;
      const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
      const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;

      // Calculate totals from items
      const itemsTotal = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0);
      const itemsAmount = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);
      const itemsDiscount = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.DiscountAmount) || 0), 0);
      const itemsTaxable = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.TaxableAmount) || 0), 0);
      const itemsCGST = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.CGSTAmount) || 0), 0);
      const itemsSGST = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.SGSTAmount) || 0), 0);
      const itemsIGST = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.IGSTAmount) || 0), 0);

      // Calculate grand total
      const tcsAmount = poData.TCSAmount || 0;
      const grandTotal = itemsTotal + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
      const roundOff = Math.round(grandTotal) - grandTotal;
      const finalTotal = Math.round(grandTotal);

      const initialFormData = {
        PurchaseOrderId: poData.Id,
        PurchaseOrderNo: poData.PurchaseOrderNo,
        PurchaseOrderDate: poData.PurchaseOrderDate?.split('T')[0] || '',
        VendorId: poData.VendorId,
        MasterName: poData.MasterName || '',
        BillNumber: '',
        BillDate: new Date().toISOString().split('T')[0],
        ClearingDate: new Date().toISOString().split('T')[0],
        GstCalculationType: poData.GstCalculationType || 1,
        GstCalculationTypeName: poData.GstCalculationTypeName || 'Excluding Tax',
        Amount: Math.round(itemsAmount * 100) / 100,
        DiscountAmount: Math.round(itemsDiscount * 100) / 100,
        TaxableAmount: Math.round(itemsTaxable * 100) / 100,
        CGSTAmount: Math.round(itemsCGST * 100) / 100,
        SGSTAmount: Math.round(itemsSGST * 100) / 100,
        IGSTAmount: Math.round(itemsIGST * 100) / 100,
        FreightAmount: freightAmount,
        FreightGSTRate: effectiveFreightGSTRate,
        FreightGSTAmount: Math.round(freightGSTAmount * 100) / 100,
        PackingAmount: packingAmount,
        PackingGSTRate: effectivePackingGSTRate,
        PackingGSTAmount: Math.round(packingGSTAmount * 100) / 100,
        TCSAmount: tcsAmount,
        RoundOff: Math.round(roundOff * 100) / 100,
        TotalAmount: finalTotal,
        PaidAmount: 0,
        BalanceAmount: finalTotal,
        Remarks: '',
        AdditionalRemarks: ''
      };

      setPOToPurchaseFormData(initialFormData);
    }

    setModalLoading(false);
    setConvertedPOModalOpen(false);
    setPOToPurchaseModalOpen(true);
  };

  const handleClosePOToPurchaseModal = () => {
    setPOToPurchaseModalOpen(false);
    setSelectedPOData(null);
    setSelectedPODetails([]);
    setPOToPurchaseFormData({});
  };

  // Handle PO Details View Modal
  const handleOpenPODetailsModal = async (poData) => {
    setModalLoading(true);

    try {
      const poDetails = await fetchPODetailsForPurchase(poData.Id);

      if (poDetails) {
        const poInfo = poDetails.PurchaseOrder || poDetails;
        const details = poDetails.PurchaseOrderDetails || [];

        setPODetailsData(poInfo);
        setPODetailsItems(details);
      } else {
        setPODetailsData(poData);
        setPODetailsItems([]);
      }
    } catch (error) {
      console.error('Error fetching PO details:', error);
      setPODetailsData(poData);
      setPODetailsItems([]);
    }

    setModalLoading(false);
    setPODetailsModalOpen(true);
  };

  const handleClosePODetailsModal = () => {
    setPODetailsModalOpen(false);
    setPODetailsData(null);
    setPODetailsItems([]);
  };

  // Handle Purchase Return Modal
  const handleOpenPurchaseReturnModal = async (purchaseItem) => {
    setModalLoading(true);

    try {
      console.log('Opening Purchase Return Modal for:', purchaseItem);
      const purchaseReturnData = await fetchPurchaseForReturn(purchaseItem.Id);
      console.log('Fetched purchase return data:', purchaseReturnData);

      if (purchaseReturnData) {
        const purchaseData = purchaseReturnData.PurchaseReturn || purchaseReturnData;
        const details = purchaseReturnData.PurchaseReturnDetails || purchaseReturnData.PurchaseDetails || [];

        console.log('Purchase data:', purchaseData);
        console.log('Purchase data keys:', Object.keys(purchaseData));
        console.log('BillNumber:', purchaseData.BillNumber);
        console.log('PurchaseNo:', purchaseData.PurchaseNo);
        console.log('Purchase return details array:', details);
        console.log('Details length:', details.length);

        setPurchaseReturnData(purchaseData);

        // Initialize return details with return quantities set to 0
        const initializedReturnDetails = details.map(d => ({
          ...d,
          ReturnQuantity: d.Quantity || 0, // Use existing return quantity if available
          ReturnAmount: d.Amount || 0,
          ReturnDiscountAmount: d.DiscountAmount || 0,
          ReturnTaxableAmount: d.TaxableAmount || 0,
          ReturnCGSTAmount: d.CGSTAmount || 0,
          ReturnSGSTAmount: d.SGSTAmount || 0,
          ReturnIGSTAmount: d.IGSTAmount || 0,
          ReturnTotalAmount: d.TotalAmount || 0,
          MaxReturnQuantity: d.PurchaseQuantity || 0, // Use PurchaseQuantity as max returnable quantity
          // Keep original fields for reference
          OriginalQuantity: d.Quantity || 0,
          OriginalAmount: d.Amount || 0,
          OriginalTaxableAmount: d.TaxableAmount || 0,
          OriginalCGSTAmount: d.CGSTAmount || 0,
          OriginalSGSTAmount: d.SGSTAmount || 0,
          OriginalIGSTAmount: d.IGSTAmount || 0,
          OriginalTotalAmount: d.TotalAmount || 0
        }));

        console.log('Final initialized return details:', initializedReturnDetails);
        setPurchaseReturnDetails(initializedReturnDetails);

        // Fetch max bill number for purchase return
        const maxBillData = await fetchPurchaseReturnMaxBillNo();
        const returnNumber = maxBillData.billPrefix && maxBillData.finYear ?
          `${maxBillData.billPrefix}/${maxBillData.billNumber}/${maxBillData.finYear}` :
          maxBillData.billNumber.toString();

        // Initialize form data for return
        // Calculate GST rates for freight and packing
        const defaultGSTRate = 18; // Default GST rate
        const purchaseDetails = details || [];
        const maxItemGSTRate = purchaseDetails.length > 0 ? Math.max(...purchaseDetails.map(d => parseFloat(d.GSTRate) || 0)) : 0;

        // Use highest GST rate if > 18%, otherwise use default 18%
        const effectiveGSTRate = maxItemGSTRate > defaultGSTRate ? maxItemGSTRate : defaultGSTRate;

        console.log('GST Rate Calculation:', {
          defaultGSTRate,
          maxItemGSTRate,
          effectiveGSTRate,
          purchaseDetails: purchaseDetails.map(d => ({ ItemName: d.ItemName, GSTRate: d.GSTRate }))
        });

        setPurchaseReturnFormData({
          PurchaseId: purchaseData.PurchaseId || purchaseData.Id,
          PurchaseNo: purchaseData.PurchaseNo || purchaseData.BillNumber || '', // Try both fields
          PurchaseBillNumber: purchaseData.BillNumber || purchaseData.PurchaseNo || '', // Keep for display
          PurchaseBillDate: purchaseData.PurchaseDate || purchaseData.BillDate,
          ReturnDate: new Date().toISOString().split('T')[0],
          ReturnNumber: returnNumber, // Auto-generated return number
          VendorId: purchaseData.VendorId,
          MasterName: purchaseData.MasterName,
          InvoiceTypeId: purchaseData.InvoiceTypeId || 0,
          GSTNumber: purchaseData.GSTNumber || '',
          BranchId: purchaseData.BranchId || branchId,
          GstCalculationType: purchaseData.GstCalculationType || 1, // 1 = Excluding Tax, 2 = Including Tax
          GstCalculationTypeName: purchaseData.GstCalculationTypeName || 'Excluding Tax',
          Amount: 0,
          DiscountAmount: 0,
          TaxableAmount: 0,
          CGSTAmount: 0,
          SGSTAmount: 0,
          IGSTAmount: 0,
          FreightAmount: 0,
          FreightGSTRate: effectiveGSTRate,
          FreightGSTAmount: 0,
          PackingAmount: 0,
          PackingGSTRate: effectiveGSTRate,
          PackingGSTAmount: 0,
          TCSAmount: 0,
          RoundOff: 0,
          TotalAmount: 0,
          PaidAmount: 0,
          BalanceAmount: 0,
          Remarks: ''
        });
      } else {
        setPurchaseReturnData(purchaseItem);
        setPurchaseReturnDetails([]);
        setPurchaseReturnFormData({});
      }
    } catch (error) {
      console.error('Error fetching purchase details for return:', error);
      setPurchaseReturnData(purchaseItem);
      setPurchaseReturnDetails([]);
      setPurchaseReturnFormData({});
    }

    setModalLoading(false);
    setPurchaseReturnModalOpen(true);
  };

  const handleClosePurchaseReturnModal = () => {
    setPurchaseReturnModalOpen(false);
    setPurchaseReturnData(null);
    setPurchaseReturnDetails([]);
    setPurchaseReturnFormData({});
  };

  // Handle Purchase Details View Modal (for bill number click)
  const handleOpenPurchaseDetailsViewModal = async (purchaseData) => {
    setModalLoading(true);

    try {
      // If called from Purchase Return modal, use PurchaseId instead of Id
      const purchaseId = purchaseData.PurchaseId || purchaseData.Id;
      console.log('Opening Purchase Details View for Purchase ID:', purchaseId);
      console.log('Purchase Data:', purchaseData);

      const purchaseDetails = await fetchSinglePurchase(purchaseId);

      if (purchaseDetails) {
        const purchaseInfo = purchaseDetails.Purchase || purchaseDetails;
        const details = purchaseDetails.PurchaseDetails || [];

        console.log('Fetched Purchase Info:', purchaseInfo);
        console.log('Fetched Purchase Details:', details);

        setPurchaseDetailsViewData(purchaseInfo);
        setPurchaseDetailsViewItems(details);
      } else {
        setPurchaseDetailsViewData(purchaseData);
        setPurchaseDetailsViewItems([]);
      }
    } catch (error) {
      console.error('Error fetching purchase details:', error);
      setPurchaseDetailsViewData(purchaseData);
      setPurchaseDetailsViewItems([]);
    }

    setModalLoading(false);
    setPurchaseDetailsViewModalOpen(true);
  };

  const handleClosePurchaseDetailsViewModal = () => {
    setPurchaseDetailsViewModalOpen(false);
    setPurchaseDetailsViewData(null);
    setPurchaseDetailsViewItems([]);
  };

  // Handle Purchase Return detail changes
  const handlePurchaseReturnDetailChange = (index, field, value) => {
    const updatedDetails = [...purchaseReturnDetails];

    // Handle non-numeric fields first
    if (!['ReturnQuantity', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      setPurchaseReturnDetails(updatedDetails);
      return;
    }

    const detail = updatedDetails[index];

    // Handle Return Quantity change
    if (field === 'ReturnQuantity') {
      // Allow empty value
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: 0 };
        calculateReturnTotals(updatedDetails);
        return;
      }

      // Strict numeric validation
      const validNumberRegex = /^[0-9]*\.?[0-9]*$/;
      if (!validNumberRegex.test(value)) {
        return;
      }

      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) {
        return;
      }

      // Check if return quantity doesn't exceed purchased quantity
      const maxReturnQty = updatedDetails[index].MaxReturnQuantity || 0;
      if (numValue > maxReturnQty) {
        return;
      }

      const returnQty = numValue;
      const rate = parseFloat(detail.Rate) || 0;
      const gstRate = parseFloat(detail.GSTRate) || 0;
      const discountPct = parseFloat(detail.DiscountPercentage) || 0;

      // Calculate return amounts
      const returnAmount = returnQty * rate;
      const returnDiscountAmount = (returnAmount * discountPct) / 100;
      const returnTaxableAmount = returnAmount - returnDiscountAmount;
      const returnGSTAmount = (returnTaxableAmount * gstRate) / 100;

      updatedDetails[index] = {
        ...updatedDetails[index],
        ReturnQuantity: returnQty,
        ReturnAmount: Math.round(returnAmount * 100) / 100,
        ReturnDiscountAmount: Math.round(returnDiscountAmount * 100) / 100,
        ReturnTaxableAmount: Math.round(returnTaxableAmount * 100) / 100,
        ReturnCGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
        ReturnSGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
        ReturnIGSTAmount: 0,
        ReturnTotalAmount: Math.round((returnTaxableAmount + returnGSTAmount) * 100) / 100
      };

      setPurchaseReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
    }

    // Handle Discount Percentage change
    else if (field === 'DiscountPercentage') {
      const numValue = parseFloat(value) || 0;
      if (numValue < 0 || numValue > 100) {
        return;
      }

      const returnQty = parseFloat(detail.ReturnQuantity) || 0;
      const rate = parseFloat(detail.Rate) || 0;
      const gstRate = parseFloat(detail.GSTRate) || 0;

      const returnAmount = returnQty * rate;
      const returnDiscountAmount = (returnAmount * numValue) / 100;
      const returnTaxableAmount = returnAmount - returnDiscountAmount;
      const returnGSTAmount = (returnTaxableAmount * gstRate) / 100;

      updatedDetails[index] = {
        ...updatedDetails[index],
        DiscountPercentage: numValue,
        ReturnDiscountAmount: Math.round(returnDiscountAmount * 100) / 100,
        ReturnTaxableAmount: Math.round(returnTaxableAmount * 100) / 100,
        ReturnCGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
        ReturnSGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
        ReturnIGSTAmount: 0,
        ReturnTotalAmount: Math.round((returnTaxableAmount + returnGSTAmount) * 100) / 100
      };

      setPurchaseReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
    }

    // Handle Discount Amount change
    else if (field === 'DiscountAmount') {
      const numValue = parseFloat(value) || 0;

      const returnQty = parseFloat(detail.ReturnQuantity) || 0;
      const rate = parseFloat(detail.Rate) || 0;
      const gstRate = parseFloat(detail.GSTRate) || 0;

      const returnAmount = returnQty * rate;
      const discountPct = returnAmount > 0 ? (numValue / returnAmount) * 100 : 0;
      const returnTaxableAmount = returnAmount - numValue;
      const returnGSTAmount = (returnTaxableAmount * gstRate) / 100;

      updatedDetails[index] = {
        ...updatedDetails[index],
        DiscountPercentage: Math.round(discountPct * 100) / 100,
        ReturnDiscountAmount: Math.round(numValue * 100) / 100,
        ReturnTaxableAmount: Math.round(returnTaxableAmount * 100) / 100,
        ReturnCGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
        ReturnSGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
        ReturnIGSTAmount: 0,
        ReturnTotalAmount: Math.round((returnTaxableAmount + returnGSTAmount) * 100) / 100
      };

      setPurchaseReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
    }
  };

  // Calculate Purchase Return totals
  const calculateReturnTotals = (details) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.ReturnAmount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.ReturnDiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.ReturnTaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.ReturnCGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.ReturnSGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.ReturnIGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.ReturnTotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    // Calculate additional charges (proportional to return amount vs original amount)
    const originalAmount = parseFloat(purchaseReturnData?.Amount) || 1;
    const returnRatio = totals.Amount / originalAmount;

    const freightAmount = Math.round((parseFloat(purchaseReturnData?.FreightAmount) || 0) * returnRatio * 100) / 100;
    const freightGSTAmount = Math.round((parseFloat(purchaseReturnData?.FreightGSTAmount) || 0) * returnRatio * 100) / 100;
    const packingAmount = Math.round((parseFloat(purchaseReturnData?.PackingAmount) || 0) * returnRatio * 100) / 100;
    const packingGSTAmount = Math.round((parseFloat(purchaseReturnData?.PackingGSTAmount) || 0) * returnRatio * 100) / 100;
    const tcsAmount = Math.round((parseFloat(purchaseReturnData?.TCSAmount) || 0) * returnRatio * 100) / 100;

    const grandTotal = totals.TotalAmount + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
    const roundOff = Math.round(grandTotal) - grandTotal;
    const finalTotal = Math.round(grandTotal);

    // Update form data with calculated totals
    setPurchaseReturnFormData(prev => ({
      ...prev,
      Amount: Math.round(totals.Amount * 100) / 100,
      DiscountAmount: Math.round(totals.DiscountAmount * 100) / 100,
      TaxableAmount: Math.round(totals.TaxableAmount * 100) / 100,
      CGSTAmount: Math.round(totals.CGSTAmount * 100) / 100,
      SGSTAmount: Math.round(totals.SGSTAmount * 100) / 100,
      IGSTAmount: Math.round(totals.IGSTAmount * 100) / 100,
      FreightAmount: freightAmount,
      FreightGSTAmount: freightGSTAmount,
      PackingAmount: packingAmount,
      PackingGSTAmount: packingGSTAmount,
      TCSAmount: tcsAmount,
      RoundOff: Math.round(roundOff * 100) / 100,
      TotalAmount: finalTotal
    }));
  };

  // Handle Purchase Return form field changes
  const handlePurchaseReturnFormChange = (field, value) => {
    setPurchaseReturnFormData(prev => {
      const updatedFormData = {
        ...prev,
        [field]: value
      };

      // If freight or packing amount changes, recalculate GST and totals
      if (field === 'FreightAmount' || field === 'PackingAmount') {
        const freightAmount = field === 'FreightAmount' ? parseFloat(value) || 0 : parseFloat(prev.FreightAmount) || 0;
        const packingAmount = field === 'PackingAmount' ? parseFloat(value) || 0 : parseFloat(prev.PackingAmount) || 0;

        // Use stored GST rates from form data
        const freightGSTRate = parseFloat(prev.FreightGSTRate) || 18;
        const packingGSTRate = parseFloat(prev.PackingGSTRate) || 18;

        // Calculate GST amounts
        const freightGSTAmount = (freightAmount * freightGSTRate) / 100;
        const packingGSTAmount = (packingAmount * packingGSTRate) / 100;

        // Update GST amounts
        updatedFormData.FreightGSTAmount = Math.round(freightGSTAmount * 100) / 100;
        updatedFormData.PackingGSTAmount = Math.round(packingGSTAmount * 100) / 100;

        // Recalculate total: items total + freight + freight GST + packing + packing GST + TCS
        const itemsTotal = parseFloat(prev.TaxableAmount) || 0;
        const itemsGSTTotal = (parseFloat(prev.CGSTAmount) || 0) + (parseFloat(prev.SGSTAmount) || 0) + (parseFloat(prev.IGSTAmount) || 0);
        const tcsAmount = parseFloat(prev.TCSAmount) || 0;

        const grandTotal = itemsTotal + itemsGSTTotal + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
        const roundOff = Math.round(grandTotal) - grandTotal;
        const finalTotal = Math.round(grandTotal);

        updatedFormData.RoundOff = Math.round(roundOff * 100) / 100;
        updatedFormData.TotalAmount = finalTotal;

        // Recalculate balance
        const paidAmount = parseFloat(prev.PaidAmount) || 0;
        updatedFormData.BalanceAmount = finalTotal - paidAmount;
      }

      // If TCS amount changes, recalculate totals
      if (field === 'TCSAmount') {
        const tcsAmount = parseFloat(value) || 0;
        updatedFormData.TCSAmount = tcsAmount; // Ensure TCS amount is set

        const itemsTotal = parseFloat(prev.TaxableAmount) || 0;
        const itemsGSTTotal = (parseFloat(prev.CGSTAmount) || 0) + (parseFloat(prev.SGSTAmount) || 0) + (parseFloat(prev.IGSTAmount) || 0);
        const freightAmount = parseFloat(prev.FreightAmount) || 0;
        const freightGSTAmount = parseFloat(prev.FreightGSTAmount) || 0;
        const packingAmount = parseFloat(prev.PackingAmount) || 0;
        const packingGSTAmount = parseFloat(prev.PackingGSTAmount) || 0;

        const grandTotal = itemsTotal + itemsGSTTotal + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
        const roundOff = Math.round(grandTotal) - grandTotal;
        const finalTotal = Math.round(grandTotal);

        updatedFormData.RoundOff = Math.round(roundOff * 100) / 100;
        updatedFormData.TotalAmount = finalTotal;

        // Recalculate balance
        const paidAmount = parseFloat(prev.PaidAmount) || 0;
        updatedFormData.BalanceAmount = finalTotal - paidAmount;
      }

      // If paid amount changes, recalculate balance
      if (field === 'PaidAmount') {
        const paidAmount = parseFloat(value) || 0;
        const totalAmount = parseFloat(prev.TotalAmount) || 0;
        updatedFormData.BalanceAmount = totalAmount - paidAmount;
      }

      return updatedFormData;
    });
  };

  // Save Purchase Return
  const handleSavePurchaseReturn = async () => {
    // Validation
    const validationErrors = [];

    if (!purchaseReturnFormData.ReturnNumber?.trim()) {
      validationErrors.push('Return Number is required');
    }

    if (!purchaseReturnFormData.ReturnDate) {
      validationErrors.push('Return Date is required');
    }

    // Check if there are items with return quantity
    const itemsWithReturnQty = purchaseReturnDetails.filter(item => (item.ReturnQuantity || 0) > 0);
    if (itemsWithReturnQty.length === 0) {
      validationErrors.push('At least one item must have return quantity greater than 0');
    }

    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating purchase return...');

    try {
      const dataToSend = {
        PurchaseReturn: {
          Id: 0,
          CompanyId: companyId,
          BranchId: purchaseReturnFormData.BranchId || branchId,
          PurchaseId: purchaseReturnFormData.PurchaseId,
          PurchaseNo: purchaseReturnFormData.PurchaseBillNumber,
          PurchaseDate: purchaseReturnFormData.PurchaseBillDate,
          BillNumber: purchaseReturnFormData.ReturnNumber,
          BillDate: purchaseReturnFormData.ReturnDate,
          VendorId: purchaseReturnFormData.VendorId,
          MasterName: purchaseReturnFormData.MasterName,
          InvoiceTypeId: purchaseReturnFormData.InvoiceTypeId || 0,
          GSTNumber: purchaseReturnFormData.GSTNumber || '',
          GstCalculationType: parseInt(purchaseReturnFormData.GstCalculationType) || 1,
          GstCalculationTypeName: purchaseReturnFormData.GstCalculationTypeName || 'Excluding Tax',
          Amount: parseFloat(purchaseReturnFormData.Amount) || 0,
          DiscountAmount: parseFloat(purchaseReturnFormData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(purchaseReturnFormData.TaxableAmount) || 0,
          CGSTAmount: parseFloat(purchaseReturnFormData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(purchaseReturnFormData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(purchaseReturnFormData.IGSTAmount) || 0,
          FreightAmount: parseFloat(purchaseReturnFormData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(purchaseReturnFormData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(purchaseReturnFormData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(purchaseReturnFormData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(purchaseReturnFormData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(purchaseReturnFormData.PackingGSTAmount) || 0,
          TCSAmount: parseFloat(purchaseReturnFormData.TCSAmount) || 0,
          RoundOff: parseFloat(purchaseReturnFormData.RoundOff) || 0,
          TotalAmount: parseFloat(purchaseReturnFormData.TotalAmount) || 0,
          PaidAmount: parseFloat(purchaseReturnFormData.PaidAmount) || 0,
          BalanceAmount: parseFloat(purchaseReturnFormData.BalanceAmount) || 0,
          Remarks: purchaseReturnFormData.Remarks || '',
          IsActive: true,
          CreatedBy: userId
        },
        PurchaseReturnDetails: itemsWithReturnQty.map(d => ({
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          PurchaseReturnId: 0, // Will be set by backend
          ItemId: d.ItemId,
          ItemName: d.ItemName || '',
          BatchNumber: d.BatchNumber || '',
          PurchaseId: purchaseReturnFormData.PurchaseId,
          PurchaseQuantity: parseFloat(d.PurchaseQuantity) || 0, // Original purchased quantity
          Quantity: parseFloat(d.ReturnQuantity) || 0, // Return quantity
          ReturnStockQuantity: parseFloat(d.ReturnQuantity) || 0, // Good return quantity
          Rate: parseFloat(d.Rate) || 0,
          Amount: parseFloat(d.ReturnAmount) || 0,
          DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(d.ReturnDiscountAmount) || 0,
          HSNCode: d.HSNCode || '',
          GSTRate: parseFloat(d.GSTRate) || 0,
          TaxableAmount: parseFloat(d.ReturnTaxableAmount) || 0,
          CGSTAmount: parseFloat(d.ReturnCGSTAmount) || 0,
          SGSTAmount: parseFloat(d.ReturnSGSTAmount) || 0,
          IGSTAmount: parseFloat(d.ReturnIGSTAmount) || 0,
          TotalAmount: parseFloat(d.ReturnTotalAmount) || 0,
          IsActive: true,
          CreatedBy: userId
        }))
      };

      await postData('PurchaseReturnApi', '', dataToSend, token);
      setSuccess('Purchase return created successfully!');
      handleClosePurchaseReturnModal();
      await fetchPurchases(); // Refresh the purchase list

      // Redirect to Purchase Return page
      setTimeout(() => {
        history.push('/PurchaseReturnAgainstIndex');
      }, 1500);
    } catch (error) {
      console.error('Error creating purchase return:', error);
      setError('Failed to create purchase return.');
    }

    setLoading(false);
  };

  // Handle PO to Purchase form field change
  const handlePOToPurchaseFormChange = (field, value) => {
    // Update the form data first
    setPOToPurchaseFormData(prev => {
      const updatedFormData = { ...prev, [field]: value };

      // If GST Type changes, recalculate all items
      if (field === 'GstCalculationType') {
        const isIncludingTax = parseInt(value) === 2;

        // Recalculate all item details based on new GST type
        const recalculatedDetails = selectedPODetails.map(detail => {
          const qty = Math.max(0, parseFloat(detail.Quantity) || 0);
          const rate = Math.max(0, parseFloat(detail.Rate) || 0);
          const gstRate = Math.max(0, parseFloat(detail.GSTRate) || 0);

          let taxableAmount, gstAmount, amount;

          if (isIncludingTax) {
            // Rate includes GST - calculate backwards
            const totalWithGst = qty * rate;
            taxableAmount = totalWithGst / (1 + gstRate / 100);
            amount = taxableAmount;

            const discountPct = parseFloat(detail.DiscountPercentage) || 0;
            const discountAmount = (taxableAmount * discountPct) / 100;

            taxableAmount = taxableAmount - discountAmount;
            gstAmount = (taxableAmount * gstRate) / 100;

            return {
              ...detail,
              Amount: Math.round(amount * 100) / 100,
              DiscountAmount: Math.round(discountAmount * 100) / 100,
              TaxableAmount: Math.round(taxableAmount * 100) / 100,
              CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
              SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
              IGSTAmount: 0,
              TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100
            };
          } else {
            // Rate excludes GST - normal calculation
            amount = qty * rate;
            const discountPct = parseFloat(detail.DiscountPercentage) || 0;
            const discountAmount = (amount * discountPct) / 100;

            taxableAmount = amount - discountAmount;
            gstAmount = (taxableAmount * gstRate) / 100;

            return {
              ...detail,
              Amount: Math.round(amount * 100) / 100,
              DiscountAmount: Math.round(discountAmount * 100) / 100,
              TaxableAmount: Math.round(taxableAmount * 100) / 100,
              CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
              SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
              IGSTAmount: 0,
              TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100
            };
          }
        });

        // Update the details
        setSelectedPODetails(recalculatedDetails);

        // Recalculate totals with new details
        setTimeout(() => {
          calculatePOTotals(recalculatedDetails);
        }, 0);
      }

      // If freight or packing amount changes, recalculate immediately
      if (field === 'FreightAmount' || field === 'PackingAmount') {
        // Get GST rates
        const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate) || 0;
        const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate) || 0;
        const maxItemGSTRate = selectedPODetails.length > 0 ? Math.max(...selectedPODetails.map(d => parseFloat(d.GSTRate) || 0)) : 0;

        const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
        const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);

        // Calculate GST amounts
        const freightAmount = field === 'FreightAmount' ? parseFloat(value) || 0 : parseFloat(updatedFormData.FreightAmount) || 0;
        const packingAmount = field === 'PackingAmount' ? parseFloat(value) || 0 : parseFloat(updatedFormData.PackingAmount) || 0;

        const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
        const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;

        // Update GST rates and amounts
        updatedFormData.FreightGSTRate = effectiveFreightGSTRate;
        updatedFormData.FreightGSTAmount = Math.round(freightGSTAmount * 100) / 100;
        updatedFormData.PackingGSTRate = effectivePackingGSTRate;
        updatedFormData.PackingGSTAmount = Math.round(packingGSTAmount * 100) / 100;

        // Recalculate total
        const itemsTotal = selectedPODetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0);
        const tcsAmount = parseFloat(updatedFormData.TCSAmount) || 0;
        const grandTotal = itemsTotal + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
        const roundOff = Math.round(grandTotal) - grandTotal;
        const finalTotal = Math.round(grandTotal);
        const paidAmount = parseFloat(updatedFormData.PaidAmount) || 0;

        updatedFormData.RoundOff = Math.round(roundOff * 100) / 100;
        updatedFormData.TotalAmount = finalTotal;
        updatedFormData.BalanceAmount = finalTotal - paidAmount;
      }

      return updatedFormData;
    });

    // Recalculate totals for other fields
    if (['TCSAmount', 'PaidAmount'].includes(field)) {
      setTimeout(() => {
        calculatePOTotals(selectedPODetails);
      }, 0);
    }
  };

  // Handle PO to Purchase detail change
  const handlePOToPurchaseDetailChange = (index, field, value) => {
    const updatedDetails = [...selectedPODetails];

    // Handle non-numeric fields first
    if (['ItemId', 'ItemName', 'HSNCode'].includes(field)) {
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      setSelectedPODetails(updatedDetails);
      // Always recalculate when ItemId changes (item selection)
      if (field === 'ItemId' || field === 'ItemName') {
        calculatePOTotals(updatedDetails);
      }
      return;
    }

    // Enhanced validation for numeric inputs
    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      // Allow empty value
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: '' };
        setSelectedPODetails(updatedDetails);
        calculatePOTotals(updatedDetails);
        return;
      }

      // Strict numeric validation - only allow numbers and decimal point
      const validNumberRegex = /^[0-9]*\.?[0-9]*$/;
      if (!validNumberRegex.test(value)) {
        // Invalid input - don't update
        return;
      }

      // Check for valid decimal number
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        // Invalid number - don't update
        return;
      }

      // Additional validation for specific fields
      if (['Quantity', 'Rate'].includes(field) && numValue < 0) {
        // Negative values not allowed for these fields
        return;
      }

      if (['DiscountPercentage'].includes(field) && (numValue < 0 || numValue > 100)) {
        // Discount percentage should be between 0-100
        return;
      }

      if (['GSTRate'].includes(field) && (numValue < 0 || numValue > 100)) {
        // GST rate should be between 0-100
        return;
      }
    }

    updatedDetails[index] = { ...updatedDetails[index], [field]: value };

    // Recalculate item totals when relevant fields change
    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      const detail = updatedDetails[index];
      const qty = Math.max(0, parseFloat(detail.Quantity) || 0);
      const rate = Math.max(0, parseFloat(detail.Rate) || 0);
      const gstRate = Math.max(0, parseFloat(detail.GSTRate) || 0);
      const isIncludingTax = (poToPurchaseFormData.GstCalculationType || 1) === 2;

      let taxableAmount, gstAmount, amount;

      if (isIncludingTax) {
        // Rate includes GST - calculate backwards
        const totalWithGst = qty * rate;
        taxableAmount = totalWithGst / (1 + gstRate / 100);
        amount = taxableAmount;

        let discountAmount = 0, discountPct = 0;
        if (field === 'DiscountAmount') {
          discountAmount = Math.max(0, parseFloat(value) || 0);
          discountPct = taxableAmount > 0 ? (discountAmount / taxableAmount) * 100 : 0;
        } else {
          discountPct = parseFloat(detail.DiscountPercentage) || 0;
          discountAmount = (taxableAmount * discountPct) / 100;
        }

        taxableAmount = taxableAmount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          Amount: Math.round(amount * 100) / 100,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
          SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
          IGSTAmount: 0,
          TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100
        };
      } else {
        // Rate excludes GST - normal calculation
        amount = qty * rate;

        let discountAmount = 0, discountPct = 0;
        if (field === 'DiscountAmount') {
          discountAmount = Math.max(0, parseFloat(value) || 0);
          discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
        } else {
          discountPct = parseFloat(detail.DiscountPercentage) || 0;
          discountAmount = (amount * discountPct) / 100;
        }

        taxableAmount = amount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          Amount: Math.round(amount * 100) / 100,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
          SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
          IGSTAmount: 0,
          TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100
        };
      }
    }

    setSelectedPODetails(updatedDetails);
    calculatePOTotals(updatedDetails);
  };

  // Calculate PO to Purchase totals
  const calculatePOTotals = (details) => {
    console.log('Calculating PO totals for details:', details);

    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    console.log('Calculated item totals:', totals);

    // Get current freight and packing amounts from form data
    const freightAmount = parseFloat(poToPurchaseFormData.FreightAmount) || 0;
    const packingAmount = parseFloat(poToPurchaseFormData.PackingAmount) || 0;

    // Get GST rates - use default rates from freight/packing GST data or highest item GST rate
    const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate) || 0;
    const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate) || 0;
    const maxItemGSTRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.GSTRate) || 0)) : 0;

    const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
    const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);

    // Calculate GST amounts
    const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
    const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;

    const tcsAmount = parseFloat(poToPurchaseFormData.TCSAmount) || 0;

    // Calculate grand total
    const grandTotal = totals.TotalAmount + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
    const roundOff = Math.round(grandTotal) - grandTotal;
    const finalTotal = Math.round(grandTotal);

    const paidAmount = parseFloat(poToPurchaseFormData.PaidAmount) || 0;
    const balanceAmount = finalTotal - paidAmount;

    // Update form data with calculated totals
    setPOToPurchaseFormData(prev => {
      const updatedFormData = {
        ...prev,
        Amount: Math.round(totals.Amount * 100) / 100,
        DiscountAmount: Math.round(totals.DiscountAmount * 100) / 100,
        TaxableAmount: Math.round(totals.TaxableAmount * 100) / 100,
        CGSTAmount: Math.round(totals.CGSTAmount * 100) / 100,
        SGSTAmount: Math.round(totals.SGSTAmount * 100) / 100,
        IGSTAmount: Math.round(totals.IGSTAmount * 100) / 100,
        FreightGSTRate: effectiveFreightGSTRate,
        FreightGSTAmount: Math.round(freightGSTAmount * 100) / 100,
        PackingGSTRate: effectivePackingGSTRate,
        PackingGSTAmount: Math.round(packingGSTAmount * 100) / 100,
        RoundOff: Math.round(roundOff * 100) / 100,
        TotalAmount: finalTotal,
        BalanceAmount: balanceAmount
      };

      console.log('Updated form data:', updatedFormData);
      return updatedFormData;
    });
  };

  // Save PO to Purchase with validation
  const handleSavePOToPurchase = async () => {
    // Validation
    const validationErrors = [];

    // Check required fields
    if (!poToPurchaseFormData.BillNumber?.trim()) {
      validationErrors.push('Bill Number is required');
    }

    if (!poToPurchaseFormData.BillDate) {
      validationErrors.push('Bill Date is required');
    }

    // Check if there are items
    if (!selectedPODetails || selectedPODetails.length === 0) {
      validationErrors.push('At least one item is required');
    }

    // Validate each item
    selectedPODetails.forEach((item, index) => {
      if (!item.ItemId) {
        validationErrors.push(`Item ${index + 1}: Please select an item`);
      }

      if (!item.Quantity || parseFloat(item.Quantity) <= 0) {
        validationErrors.push(`Item ${index + 1}: Quantity must be greater than 0`);
      }

      if (!item.Rate || parseFloat(item.Rate) <= 0) {
        validationErrors.push(`Item ${index + 1}: Rate must be greater than 0`);
      }
    });

    // Check if total amount is greater than 0
    if (!poToPurchaseFormData.TotalAmount || parseFloat(poToPurchaseFormData.TotalAmount) <= 0) {
      validationErrors.push('Total amount must be greater than 0');
    }

    // Show validation errors
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating purchase from PO...');

    try {
      const dataToSend = {
        Purchase: {
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          VendorId: poToPurchaseFormData.VendorId,
          MasterName: poToPurchaseFormData.MasterName,
          GstCalculationType: poToPurchaseFormData.GstCalculationType || 1,
          GstCalculationTypeName: poToPurchaseFormData.GstCalculationTypeName || 'Excluding Tax',
          PurchaseOrderId: poToPurchaseFormData.PurchaseOrderId,
          PurchaseOrderNo: poToPurchaseFormData.PurchaseOrderNo,
          PurchaseOrderDate: poToPurchaseFormData.PurchaseOrderDate,
          BillNumber: poToPurchaseFormData.BillNumber,
          BillDate: poToPurchaseFormData.BillDate,
          ClearingDate: poToPurchaseFormData.ClearingDate || null,
          Amount: parseFloat(poToPurchaseFormData.Amount) || 0,
          DiscountAmount: parseFloat(poToPurchaseFormData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(poToPurchaseFormData.TaxableAmount) || 0,
          CGSTAmount: parseFloat(poToPurchaseFormData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(poToPurchaseFormData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(poToPurchaseFormData.IGSTAmount) || 0,
          FreightAmount: parseFloat(poToPurchaseFormData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(poToPurchaseFormData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(poToPurchaseFormData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(poToPurchaseFormData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(poToPurchaseFormData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(poToPurchaseFormData.PackingGSTAmount) || 0,
          TCSAmount: parseFloat(poToPurchaseFormData.TCSAmount) || 0,
          RoundOff: parseFloat(poToPurchaseFormData.RoundOff) || 0,
          TotalAmount: parseFloat(poToPurchaseFormData.TotalAmount) || 0,
          PaidAmount: parseFloat(poToPurchaseFormData.PaidAmount) || 0,
          BalanceAmount: (parseFloat(poToPurchaseFormData.TotalAmount) || 0) - (parseFloat(poToPurchaseFormData.PaidAmount) || 0),
          Remarks: poToPurchaseFormData.Remarks || '',
          AdditionalRemarks: poToPurchaseFormData.AdditionalRemarks || '',
          IsActive: true,
          CreatedBy: userId
        },
        PurchaseDetails: selectedPODetails.map(d => ({
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          ItemId: d.ItemId,
          ItemName: d.ItemName || '',
          PurchaseOrderId: poToPurchaseFormData.PurchaseOrderId,
          Quantity: parseFloat(d.Quantity) || 0,
          Rate: parseFloat(d.Rate) || 0,
          Amount: parseFloat(d.Amount) || 0,
          DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(d.DiscountAmount) || 0,
          HSNCode: d.HSNCode || '',
          GSTRate: parseFloat(d.GSTRate) || 0,
          TaxableAmount: parseFloat(d.TaxableAmount) || 0,
          CGSTAmount: parseFloat(d.CGSTAmount) || 0,
          SGSTAmount: parseFloat(d.SGSTAmount) || 0,
          IGSTAmount: parseFloat(d.IGSTAmount) || 0,
          TotalAmount: parseFloat(d.TotalAmount) || 0,
          IsActive: true,
          CreatedBy: userId
        }))
      };

      await postData('PurchaseApi', '', dataToSend, token);
      setSuccess('Purchase created successfully from PO!');
      handleClosePOToPurchaseModal();
      await fetchPurchases();
    } catch (error) {
      console.error('Error creating purchase from PO:', error);
      setError('Failed to create purchase from PO.');
    }

    setLoading(false);
  };

  // Activate handlers
  const handleOpenActivateModal = (id, billNo) => {
    setActivateItemId(id);
    setActivateItemNo(billNo);
    setActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setActivateItemId(null);
    setActivateItemNo('');
  };

  const handleActivate = async () => {
    if (!activateItemId) return;
    setLoading(true);
    handleCloseActivateModal();
    setLoadingMessage('Activating purchase...');
    try {
      await activateData('PurchaseApi', `${activateItemId}`, token);
      setSuccess('Purchase activated successfully!');
      await fetchAllData();
    } catch (error) {
      console.error('Error activating record:', error);
      setError('Failed to activate purchase.');
    }
    setLoading(false);
  };

  // Inactive view modal - separate styled modal like PurchaseOrder
  const handleOpenInactiveModal = async (item) => {
    setModalLoading(true);

    const freshData = await fetchInactivePurchaseDetails(item.Id);

    if (freshData) {
      const purchaseData = freshData.Purchase || freshData;
      const details = freshData.PurchaseDetails || [];
      setInactiveViewData(purchaseData);
      setInactiveViewDetails(details);
    } else {
      setInactiveViewData(item);
      setInactiveViewDetails([]);
    }

    setModalLoading(false);
    setInactiveModalOpen(true);
  };

  const handleCloseInactiveModal = () => {
    setInactiveModalOpen(false);
    setInactiveViewData(null);
    setInactiveViewDetails([]);
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setModalLoading(true);
      const freshData = await fetchSinglePurchase(itemId);

      if (freshData) {
        const p = freshData.Purchase || freshData;
        const details = freshData.PurchaseDetails || [];
        const supplierName = p.MasterName || suppliers.find(s => (s.VendorId || s.Id) === p.VendorId)?.Name || '';
        const supplier = suppliers.find(s => (s.VendorId || s.Id) === p.VendorId);
        const invoiceTypeName = p.InvoiceTypeName || supplier?.InvoiceTypeName || '';

        setSelectedItem(p);
        setFormData({
          Id: p.Id,
          CompanyId: p.CompanyId || companyId,
          BranchId: p.BranchId || branchId,
          VendorId: p.VendorId,
          MasterName: supplierName,
          InvoiceTypeId: p.InvoiceTypeId || 0,
          InvoiceTypeName: invoiceTypeName,
          GSTNumber: p.GSTNumber || '',
          // PO Reference
          PurchaseOrderId: p.PurchaseOrderId || null,
          PurchaseOrderNo: p.PurchaseOrderNo || '',
          PurchaseOrderDate: p.PurchaseOrderDate?.split('T')[0] || '',
          // Purchase Details
          BillDate: p.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          ClearingDate: p.ClearingDate?.split('T')[0] || '',
          BillNumber: p.BillNumber || '',
          // Calculation Fields
          TotalQuantity: p.TotalQuantity || 0,
          Amount: p.Amount || 0,
          CGSTAmount: p.CGSTAmount || 0,
          SGSTAmount: p.SGSTAmount || 0,
          IGSTAmount: p.IGSTAmount || 0,
          FreightAmount: p.FreightAmount || 0,
          FreightGSTRate: p.FreightGSTRate || 0,
          FreightGSTAmount: p.FreightGSTAmount || 0,
          PackingAmount: p.PackingAmount || 0,
          PackingGSTRate: p.PackingGSTRate || 0,
          PackingGSTAmount: p.PackingGSTAmount || 0,
          TCSAmount: p.TCSAmount || 0,
          DiscountAmount: p.DiscountAmount || 0,
          RoundOff: p.RoundOff || 0,
          TotalAmount: p.TotalAmount || 0,
          PaidAmount: p.PaidAmount || 0,
          BalanceAmount: p.BalanceAmount || 0,
          Remarks: p.Remarks || '',
          IsActive: p.IsActive ?? true,
          IsVerified: p.IsVerified ?? false,
          IsConvertToGoodReceipt: p.IsConvertToGoodReceipt ?? false,
          CreatedBy: p.CreatedBy,
          CreatedByName: p.CreatedByName || '',
          CreatedDate: p.CreatedDate,
          ModifiedBy: p.ModifiedBy,
          ModifiedByName: p.ModifiedByName || '',
          ModifiedDate: p.ModifiedDate
        });

        setPurchaseDetails(details.map(d => ({
          Id: d.Id || 0,
          PurchaseId: d.PurchaseId || 0,
          CompanyId: d.CompanyId || companyId,
          BranchId: d.BranchId || branchId,
          ItemId: d.ItemId,
          ItemName: d.ItemName || items.find(i => i.Id === d.ItemId)?.Name || '',
          BatchNumber: d.BatchNumber || '',
          PurchaseOrderId: d.PurchaseOrderId || null,
          // Quantity fields
          Quantity: d.Quantity || 0,          
          // Calculation fields
          Rate: d.Rate || 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          TaxableAmount: d.TaxableAmount || 0,
          CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0,
          IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: d.CreatedBy,
          CreatedDate: d.CreatedDate,
          ModifiedBy: d.ModifiedBy,
          ModifiedDate: d.ModifiedDate
        })));
      } else {
        setSelectedItem(null);
        setFormData({});
        setPurchaseDetails([]);
      }
      setModalLoading(false);
    } else {
      // Create mode
      setModalLoading(true);
      const maxBillData = await fetchMaxBillNo();
      const gstData = await fetchFreightAndPackingGST();
      const freightGSTRate = gstData.freight?.GSTRate || 0;
      const packingGSTRate = gstData.packing?.GSTRate || 0;

      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        VendorId: '',
        InvoiceTypeId: 0,
        GSTNumber: '',
        PurchaseOrderId: null,
        PurchaseOrderNo: '',
        PurchaseOrderDate: '',
        BillDate: new Date().toISOString().split('T')[0],
        ClearingDate: new Date().toISOString().split('T')[0],
        BillNumber: '',
        TotalQuantity: 0,
        Amount: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        FreightAmount: 0,
        FreightGSTRate: freightGSTRate,
        FreightGSTAmount: 0,
        PackingAmount: 0,
        PackingGSTRate: packingGSTRate,
        PackingGSTAmount: 0,
        TCSAmount: 0,
        DiscountAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        PaidAmount: 0,
        BalanceAmount: 0,
        Remarks: '',
        IsGST: true,
        IsActive: true,
        IsVerified: false,
        IsConvertToGoodReceipt: false
      });
      setPurchaseDetails([]);
      setModalLoading(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setPurchaseDetails([]);
  };

  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} purchase...`);

      const selectedSupplier = suppliers.find(s => (s.VendorId || s.Id) === parseInt(formData.VendorId));
      const VendorId = selectedSupplier?.VendorId || selectedSupplier?.Id || 0;
      const masterName = selectedSupplier?.Name || '';
      const invoiceTypeId = selectedSupplier?.InvoiceTypeId || formData.InvoiceTypeId || 0;
      const gstNumber = selectedSupplier?.GSTNumber || selectedSupplier?.GSTNo || '';

      const totalQuantity = purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.Quantity) || 0), 0);

      const dataToSend = {
        Purchase: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || branchId,
          VendorId: VendorId,
          MasterName: masterName,
          InvoiceTypeId: invoiceTypeId,
          GSTNumber: gstNumber,
          GstCalculationType: formData.GstCalculationType || 1,
          GstCalculationTypeName: formData.GstCalculationTypeName || 'Excluding Tax',
          // PO Reference
          PurchaseOrderId: formData.PurchaseOrderId || null,
          PurchaseOrderNo: formData.PurchaseOrderNo || '',
          PurchaseOrderDate: formData.PurchaseOrderDate || null,
          // Purchase Details
          BillDate: formData.BillDate || new Date().toISOString().split('T')[0],
          ClearingDate: formData.ClearingDate || null,
          BillNumber: formData.BillNumber || '',
          // Calculation Fields
          TotalQuantity: totalQuantity,
          Amount: parseFloat(formData.Amount) || 0,
          CGSTAmount: parseFloat(formData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(formData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(formData.IGSTAmount) || 0,
          FreightAmount: parseFloat(formData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(formData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(formData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(formData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(formData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(formData.PackingGSTAmount) || 0,
          TCSAmount: parseFloat(formData.TCSAmount) || 0,
          DiscountAmount: parseFloat(formData.DiscountAmount) || 0,
          RoundOff: Math.round((parseFloat(formData.RoundOff) || 0) * 100) / 100,
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          PaidAmount: parseFloat(formData.PaidAmount) || 0,
          BalanceAmount: parseFloat(formData.BalanceAmount) || 0,
          Remarks: formData.Remarks || '',
          IsActive: formData.IsActive ?? true,
          IsVerified: formData.IsVerified ?? false,
          IsConvertToGoodReceipt: formData.IsConvertToGoodReceipt ?? false,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          CreatedDate: modalAction === 'edit' ? formData.CreatedDate : null,
          ModifiedBy: modalAction === 'edit' ? userId : null
        },
        PurchaseDetails: purchaseDetails.map(d => {
          const selectedItem = items.find(i => i.Id === parseInt(d.ItemId));
          return {
            Id: modalAction === 'edit' ? (d.Id || 0) : 0,
            PurchaseId: modalAction === 'edit' ? formData.Id : 0,
            CompanyId: companyId,
            BranchId: formData.BranchId || branchId,
            ItemId: parseInt(d.ItemId) || 0,
            ItemName: selectedItem?.Name || d.ItemName || '',
            BatchNumber: d.BatchNumber || '',
            PurchaseOrderId: d.PurchaseOrderId || null,
            // Quantity field
            Quantity: parseFloat(d.Quantity) || 0,
            // Calculation fields
            Rate: parseFloat(d.Rate) || 0,
            Amount: parseFloat(d.Amount) || 0,
            DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
            DiscountAmount: parseFloat(d.DiscountAmount) || 0,
            HSNCode: d.HSNCode || '',
            GSTRate: parseFloat(d.GSTRate) || 0,
            TaxableAmount: parseFloat(d.TaxableAmount) || 0,
            CGSTAmount: parseFloat(d.CGSTAmount) || 0,
            SGSTAmount: parseFloat(d.SGSTAmount) || 0,
            IGSTAmount: parseFloat(d.IGSTAmount) || 0,
            TotalAmount: parseFloat(d.TotalAmount) || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: modalAction === 'create' ? userId : d.CreatedBy,
            CreatedDate: modalAction === 'edit' ? d.CreatedDate : null,
            ModifiedBy: modalAction === 'edit' ? userId : null
          };
        })
      };

      if (modalAction === 'create') {
        await postData('PurchaseApi', dataToSend, token);
        setSuccess('Purchase created successfully!');
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Purchase ID is missing');
        await updateData('PurchaseApi', formData.Id, dataToSend, token);
        setSuccess('Purchase updated successfully!');
      }

      handleCloseModal();
      await fetchPurchases();
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} purchase. Please try again.`);
      setLoading(false);
    }
  };

  // Supplier drill-down handler
  const handleSupplierClick = async (VendorId, supplierName) => {
    setLoading(true);
    setLoadingMessage('Fetching supplier purchases...');
    try {
      const response = await fetchData('PurchaseApi', `/PurchaseHistoryByMaster/${VendorId}/${branchId}`, token);
      const responseData = response.response_data || response || [];
      let purchasesList = [], purchaseDetailsList = [];
      if (Array.isArray(responseData) && responseData.length > 0) {
        purchasesList = responseData[0].Purchase || [];
        purchaseDetailsList = responseData[0].PurchaseDetails || [];
      }
      const purchasesWithDetails = purchasesList.map(p => ({
        ...p,
        PurchaseDetails: purchaseDetailsList.filter(d => d.PurchaseId === p.Id)
      }));
      setSelectedSupplier({ id: VendorId, name: supplierName });
      setSupplierPurchases(purchasesWithDetails);
      setExpandedPurchaseId(null);
      setSupplierModalOpen(true);
    } catch (error) {
      console.error('Error fetching supplier purchases:', error);
      const filteredPurchases = purchases.filter(p => p.VendorId === VendorId);
      setSelectedSupplier({ id: VendorId, name: supplierName });
      setSupplierPurchases(filteredPurchases);
      setSupplierModalOpen(true);
    }
    setLoading(false);
  };

  const handleCloseSupplierModal = () => {
    setSupplierModalOpen(false);
    setSelectedSupplier(null);
    setSupplierPurchases([]);
    setExpandedPurchaseId(null);
  };

  const togglePurchaseExpand = (purchaseId) => {
    setExpandedPurchaseId(expandedPurchaseId === purchaseId ? null : purchaseId);
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, billNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(billNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;
    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('PurchaseApi', deleteItemId, token);
      setSuccess('Purchase deleted successfully!');
      await fetchPurchases();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete purchase.');
    }
    setLoading(false);
  };

  // Convert to Good Receipt handlers
  const handleOpenConvertModal = (id, billNo) => {
    setConvertItemId(id);
    setConvertItemNo(billNo);
    setConvertModalOpen(true);
  };

  const handleCloseConvertModal = () => {
    setConvertModalOpen(false);
    setConvertItemId(null);
    setConvertItemNo('');
  };

  const handleToGoodReceipt = async () => {
    if (!convertItemId) return;
    setLoading(true);
    handleCloseConvertModal();
    setLoadingMessage('Converting to Good Receipt...');
    try {
      await ConvertToGoodReceipt('PurchaseApi', convertItemId, token);
      setSuccess('Purchase converted to Good Receipt successfully!');
      handleCloseModal(); // Close the modal after successful conversion
      await fetchPurchases();
    } catch (error) {
      console.error('Error converting to Good Receipt:', error);
      setError('Failed to convert purchase to Good Receipt');
    }
    setLoading(false);
  };

  const handleVerifyFromModal = async (purchaseId) => {
    // Find the purchase item by ID
    const purchaseItem = purchases.find(p => p.Id === purchaseId);
    if (purchaseItem) {
      await handleOpenVerifyModal(purchaseItem);
    }
  };

  // Handle Update & Save from Good Receipt Modal (same as edit function)
  const handleUpdateFromGoodReceipt = async (purchaseId, updatedPurchaseDetails) => {
    if (!updatedPurchaseDetails || !verifyPurchaseData) {
      setError('Purchase details are required for update.');
      return;
    }

    setLoading(true);
    setLoadingMessage('Updating Good Receipt details...');

    try {
      // Use the same logic as handleSave but for updating from Good Receipt modal
      const selectedSupplier = suppliers.find(s => (s.VendorId || s.Id) === parseInt(verifyPurchaseData.VendorId));
      const VendorId = selectedSupplier?.VendorId || selectedSupplier?.Id || 0;
      const masterName = selectedSupplier?.Name || '';
      const invoiceTypeId = selectedSupplier?.InvoiceTypeId || verifyPurchaseData.InvoiceTypeId || 0;
      const gstNumber = selectedSupplier?.GSTNumber || selectedSupplier?.GSTNo || '';

      const totalQuantity = updatedPurchaseDetails.reduce((sum, d) => sum + (parseFloat(d.ReceivedQuantity) || 0), 0);

      const dataToSend = {
        Purchase: {
          Id: verifyPurchaseData.Id,
          CompanyId: companyId,
          BranchId: verifyPurchaseData.BranchId || branchId,
          VendorId: VendorId,
          MasterName: masterName,
          InvoiceTypeId: invoiceTypeId,
          GSTNumber: gstNumber,
          GstCalculationType: verifyPurchaseData.GstCalculationType || 1,
          GstCalculationTypeName: verifyPurchaseData.GstCalculationTypeName || 'Excluding Tax',
          // PO Reference
          PurchaseOrderId: verifyPurchaseData.PurchaseOrderId || null,
          PurchaseOrderNo: verifyPurchaseData.PurchaseOrderNo || '',
          PurchaseOrderDate: verifyPurchaseData.PurchaseOrderDate || null,
          // Purchase Details
          BillDate: verifyPurchaseData.BillDate || new Date().toISOString().split('T')[0],
          ClearingDate: verifyPurchaseData.ClearingDate || null,
          BillNumber: verifyPurchaseData.BillNumber || '',
          // Calculation Fields
          TotalQuantity: totalQuantity,
          Amount: parseFloat(verifyPurchaseData.Amount) || 0,
          CGSTAmount: parseFloat(verifyPurchaseData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(verifyPurchaseData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(verifyPurchaseData.IGSTAmount) || 0,
          FreightAmount: parseFloat(verifyPurchaseData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(verifyPurchaseData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(verifyPurchaseData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(verifyPurchaseData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(verifyPurchaseData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(verifyPurchaseData.PackingGSTAmount) || 0,
          TCSAmount: parseFloat(verifyPurchaseData.TCSAmount) || 0,
          DiscountAmount: parseFloat(verifyPurchaseData.DiscountAmount) || 0,
          RoundOff: Math.round((parseFloat(verifyPurchaseData.RoundOff) || 0) * 100) / 100,
          TotalAmount: parseFloat(verifyPurchaseData.TotalAmount) || 0,
          PaidAmount: parseFloat(verifyPurchaseData.PaidAmount) || 0,
          BalanceAmount: parseFloat(verifyPurchaseData.BalanceAmount) || 0,
          Remarks: verifyPurchaseData.Remarks || '',
          IsActive: verifyPurchaseData.IsActive ?? true,
          IsVerified: verifyPurchaseData.IsVerified ?? false,
          IsConvertToGoodReceipt: true, // Always set to true when updating from Good Receipt modal
          CreatedBy: verifyPurchaseData.CreatedBy,
          CreatedDate: verifyPurchaseData.CreatedDate,
          ModifiedBy: userId
        },
        PurchaseDetails: updatedPurchaseDetails.map(d => {
          const selectedItem = items.find(i => i.Id === parseInt(d.ItemId));
          return {
            Id: d.Id || 0,
            PurchaseId: verifyPurchaseData.Id,
            CompanyId: companyId,
            BranchId: verifyPurchaseData.BranchId || branchId,
            ItemId: parseInt(d.ItemId) || 0,
            ItemName: selectedItem?.Name || d.ItemName || '',
            BatchNumber: d.BatchNumber || '',
            PurchaseOrderId: d.PurchaseOrderId || null,
            // Quantity fields
            OrderQuantity: parseFloat(d.OrderQuantity) || 0,
            ReceivedQuantity: parseFloat(d.ReceivedQuantity) || 0,
            BilledQuantity: parseFloat(d.BilledQuantity) || 0,
            DamageQuantity: parseFloat(d.DamageQuantity) || 0,
            ExcessQuantity: parseFloat(d.ExcessQuantity) || 0,
            LessQuantity: parseFloat(d.LessQuantity) || 0,
            StockQuantity: parseFloat(d.StockQuantity) || 0,
            ReturnDamageQuantity: parseFloat(d.ReturnDamageQuantity) || 0,
            ReturnStockQuantity: parseFloat(d.ReturnStockQuantity) || 0,
            // Calculation fields
            Rate: parseFloat(d.Rate) || 0,
            Amount: parseFloat(d.Amount) || 0,
            DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
            DiscountAmount: parseFloat(d.DiscountAmount) || 0,
            HSNCode: d.HSNCode || '',
            GSTRate: parseFloat(d.GSTRate) || 0,
            TaxableAmount: parseFloat(d.TaxableAmount) || 0,
            CGSTAmount: parseFloat(d.CGSTAmount) || 0,
            SGSTAmount: parseFloat(d.SGSTAmount) || 0,
            IGSTAmount: parseFloat(d.IGSTAmount) || 0,
            TotalAmount: parseFloat(d.TotalAmount) || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: d.CreatedBy,
            CreatedDate: d.CreatedDate,
            ModifiedBy: userId
          };
        })
      };

      // Use the same update API as edit function
      await updateData('PurchaseApi', verifyPurchaseData.Id, dataToSend, token);
      setSuccess('Good Receipt updated successfully! Changes have been saved.');

      // Refresh the purchase data to show updated values
      const freshData = await fetchSinglePurchase(verifyPurchaseData.Id);
      if (freshData) {
        const p = freshData.Purchase || freshData;
        const details = freshData.PurchaseDetails || [];
        setVerifyPurchaseData(p);
        setVerifyPurchaseDetails(details);
      }

      await fetchPurchases();

    } catch (error) {
      console.error('Error updating purchase:', error);
      setError('Failed to update purchase. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle actual verification (with confirmation)
  const handleActualVerify = async (purchaseId, updatedPurchaseDetails = null) => {
    if (!purchaseId) return;

    setLoading(true);
    setLoadingMessage('Processing Good Receipt verification...');

    try {
      // First update the purchase details if any changes were made
      if (updatedPurchaseDetails && updatedPurchaseDetails.length > 0) {
        await handleUpdateFromGoodReceipt(purchaseId, updatedPurchaseDetails);
      }

      // Then verify the purchase (this will create stock entries)
      await ConvertPurchaseVerify('PurchaseApi', purchaseId, token);
      setSuccess('Good Receipt verified successfully! Stock has been updated and purchase is now complete.');
      handleCloseVerifyModal();
      await fetchPurchases();

    } catch (error) {
      console.error('Error verifying purchase:', error);
      setError('Failed to verify Good Receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify handler
  const handleVerify = async (purchaseId, updatedPurchaseDetails = null) => {
    if (!updatedPurchaseDetails || !verifyPurchaseData) {
      setError('Purchase details are required for verification.');
      return;
    }

    setLoading(true);
    setLoadingMessage('Processing good receipt...');

    try {
      // Prepare the PurchaseVerifyVM structure
      const verifyData = {
        Purchase: {
          ...verifyPurchaseData,
          IsVerified: true,
          ModifiedBy: userId,
          ModifiedDate: new Date().toISOString()
        },
        PurchaseDetails: updatedPurchaseDetails.map(detail => ({
          ...detail,
          ModifiedBy: userId,
          ModifiedDate: new Date().toISOString()
        })),
        // Stock: updatedPurchaseDetails.map(detail => ({
        //   Id: 0, // New stock entry
        //   CompanyId: companyId,
        //   BranchId: branchId,
        //   PurchaseDetailsId: detail.Id,
        //   StockType: 1, // 1 for Purchase
        //   ItemId: detail.ItemId,
        //   UOMId: null, // You can set this if you have UOM data
        //   Quantity: parseFloat(detail.StockQuantity) || 0,
        //   BalanceQuantity: parseFloat(detail.StockQuantity) || 0,
        //   DamageQuantity: parseFloat(detail.DamageQuantity) || 0,
        //   DamageBalanceQuantity: parseFloat(detail.DamageQuantity) || 0,
        //   CGSTRate: parseFloat(detail.GSTRate) / 2 || 0, // Assuming CGST is half of total GST
        //   SGSTRate: parseFloat(detail.GSTRate) / 2 || 0, // Assuming SGST is half of total GST
        //   HSNCode: detail.HSNCode || '',
        //   BatchNumber: detail.BatchNumber || '',
        //   IsReturnable: true, // Default values - you can customize these
        //   ReturnDays: 30,
        //   HasWarranty: false,
        //   WarrantyDays: 0,
        //   StockInDate: new Date().toISOString().split('T')[0],
        //   ReturnDate: null,
        //   WarrantyEndDate: null,
        //   PurchaseRate: parseFloat(detail.Rate) || 0,
        //   IsActive: true,
        //   CreatedBy: userId,
        //   CreatedDate: new Date().toISOString()
        // }))
      };

      // Use the new API endpoint with PurchaseVerifyVM
      const response = await fetch(`${apiConfig.apiUrl}PurchaseApi/${purchaseId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(verifyData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      setSuccess('Good receipt processed successfully with stock updated!');
      handleCloseVerifyModal();
      await fetchPurchases();

    } catch (error) {
      console.error('Error verifying purchase:', error);
      setError('Failed to process good receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify modal handlers
  const handleOpenVerifyModal = async (item) => {
    setModalLoading(true);

    const freshData = await fetchSinglePurchase(item.Id);

    if (freshData) {
      const p = freshData.Purchase || freshData;
      const details = freshData.PurchaseDetails || [];
      const supplierName = p.MasterName || suppliers.find(s => (s.VendorId || s.Id) === p.VendorId)?.Name || '';
      const supplier = suppliers.find(s => (s.VendorId || s.Id) === p.VendorId);
      const invoiceTypeName = p.InvoiceTypeName || supplier?.InvoiceTypeName || '';

      setVerifyPurchaseData({
        Id: p.Id,
        CompanyId: p.CompanyId || companyId,
        BranchId: p.BranchId || branchId,
        VendorId: p.VendorId,
        MasterName: supplierName,
        InvoiceTypeId: p.InvoiceTypeId || 0,
        InvoiceTypeName: invoiceTypeName,
        GSTNumber: p.GSTNumber || '',
        BillDate: p.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
        ClearingDate: p.ClearingDate?.split('T')[0] || '',
        BillNumber: p.BillNumber || '',
        TotalQuantity: p.TotalQuantity || 0,
        Amount: p.Amount || 0,
        CGSTAmount: p.CGSTAmount || 0,
        SGSTAmount: p.SGSTAmount || 0,
        IGSTAmount: p.IGSTAmount || 0,
        FreightAmount: p.FreightAmount || 0,
        FreightGSTRate: p.FreightGSTRate || 0,
        FreightGSTAmount: p.FreightGSTAmount || 0,
        PackingAmount: p.PackingAmount || 0,
        PackingGSTRate: p.PackingGSTRate || 0,
        PackingGSTAmount: p.PackingGSTAmount || 0,
        TCSAmount: p.TCSAmount || 0,
        DiscountAmount: p.DiscountAmount || 0,
        RoundOff: p.RoundOff || 0,
        TotalAmount: p.TotalAmount || 0,
        PaidAmount: p.PaidAmount || 0,
        BalanceAmount: p.BalanceAmount || 0,
        Remarks: p.Remarks || '',
        IsActive: p.IsActive ?? true,
        IsVerified: p.IsVerified ?? false,
        CreatedBy: p.CreatedBy,
        CreatedByName: p.CreatedByName || '',
        CreatedDate: p.CreatedDate,
        ModifiedBy: p.ModifiedBy,
        ModifiedByName: p.ModifiedByName || '',
        ModifiedDate: p.ModifiedDate
      });

      setVerifyPurchaseDetails(details.map(d => ({
        Id: d.Id || 0,
        PurchaseId: d.PurchaseId || 0,
        CompanyId: d.CompanyId || companyId,
        BranchId: d.BranchId || branchId,
        ItemId: d.ItemId,
        ItemName: d.ItemName || items.find(i => i.Id === d.ItemId)?.Name || '',
        BatchNumber: d.BatchNumber || '',
        ReceivedQuantity: d.ReceivedQuantity || 0,
        BilledQuantity: d.BilledQuantity || 0,
        DamageQuantity: d.DamageQuantity || 0,
        ExcessQuantity: d.ExcessQuantity || 0,
        LessQuantity: d.LessQuantity || 0,
        StockQuantity: d.StockQuantity || 0,
        ReturnDamageQuantity: d.ReturnDamageQuantity || 0,
        ReturnStockQuantity: d.ReturnStockQuantity || 0,
        Rate: d.Rate || 0,
        Amount: d.Amount || 0,
        DiscountPercentage: d.DiscountPercentage || 0,
        DiscountAmount: d.DiscountAmount || 0,
        HSNCode: d.HSNCode || '',
        GSTRate: d.GSTRate || 0,
        TaxableAmount: d.TaxableAmount || 0,
        CGSTAmount: d.CGSTAmount || 0,
        SGSTAmount: d.SGSTAmount || 0,
        IGSTAmount: d.IGSTAmount || 0,
        TotalAmount: d.TotalAmount || 0,
        IsActive: d.IsActive ?? true,
        CreatedBy: d.CreatedBy,
        CreatedDate: d.CreatedDate,
        ModifiedBy: d.ModifiedBy,
        ModifiedDate: d.ModifiedDate
      })));
    } else {
      setVerifyPurchaseData(null);
      setVerifyPurchaseDetails([]);
    }

    setModalLoading(false);
    setVerifyModalOpen(true);
  };

  const handleCloseVerifyModal = () => {
    setVerifyModalOpen(false);
    setVerifyPurchaseData(null);
    setVerifyPurchaseDetails([]);
  };

  // Filter and pagination
  const getFilteredData = (data) => {
    if (!Array.isArray(data)) return [];
    let filtered = data;
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.BillNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.MasterName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        suppliers.find(s => s.VendorId === item.VendorId)?.Name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => filterStatus === 'active' ? item.IsActive : !item.IsActive);
    }
    if (filterVerified !== 'all') {
      if (filterVerified === 'verified') {
        filtered = filtered.filter(item => item.IsConvertToGoodReceipt && !item.IsVerified);
      } else if (filterVerified === 'verified-status') {
        filtered = filtered.filter(item => item.IsVerified);
      } else if (filterVerified === 'pending') {
        filtered = filtered.filter(item => !item.IsConvertToGoodReceipt);
      }
    }
    return filtered;
  };

  const getPaginatedData = (data) => {
    const filtered = getFilteredData(data) || [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };

  const formatCurrency = (amt) => `₹${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getSupplierName = (item) => {
    if (item.MasterName) return item.MasterName;
    const supplier = suppliers.find(s => (s.VendorId || s.Id) === item.VendorId);
    return supplier?.Name || '-';
  };

  const { data: paginatedData, totalCount, totalPages, startIndex} = getPaginatedData(purchases);

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
        `}</style>

        <div className="suite-page-panel" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: "Segoe UI, sans-serif",
        }}>
          {/* Header Section */}
          <div className="suite-section-heading" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: "Segoe UI, sans-serif",
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: "Segoe UI, sans-serif", }}>Purchase Management</span>
                </h4>
              </div>
            </div>
          </div>

        

          {/* Date Filters, Search and Create Button Row - Horizontally Scrollable */}
          <div style={{ 
            marginBottom: '12px', 
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin'
          }}>
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              alignItems: 'flex-end',
              minWidth: 'fit-content',
              paddingBottom: '4px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: "#0d766e" },
                      '&.Mui-focused fieldset': { borderColor: "#0d766e" },
                    },
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: "#0d766e" },
                      '&.Mui-focused fieldset': { borderColor: "#0d766e" },
                    },
                  }}
                />
              </div>
              
              {/* Search - Icon on mobile, always visible on desktop */}
              {isMobile ? (
                searchExpanded ? (
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    autoFocus
                    onBlur={() => {
                      if (!searchTerm) {
                        setSearchExpanded(false);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: '250px',
                      width: '350px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        fontSize: '12px',
                        height: '32px',
                        '&:hover fieldset': {
                          borderColor: "#0d766e",
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: "#0d766e",
                        },
                      },
                    }}
                  />
                ) : (
                  <Tooltip title="Search" arrow>
                    <Button
                      onClick={() => setSearchExpanded(true)}
                      style={{
                        minWidth: '32px',
                        width: '32px',
                        height: '32px',
                        padding: '0',
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SearchIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                    </Button>
                  </Tooltip>
                )
              ) : (
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: '250px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '12px',
                      height: '32px',
                      '&:hover fieldset': {
                        borderColor: "#0d766e",
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: "#0d766e",
                      },
                    },
                  }}
                />
              )}

              {hasCreateMasterPermission && (
                <Tooltip title="Create a new purchase" arrow>
                  <Button
                    variant="contained"
                    onClick={() => handleOpenModal('create')}
                    className="action-button"
                    style={{
                      background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                      color: 'white',
                      padding: isMobile ? '0' : '4px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '700',
                      boxShadow: "0 2px 8px rgba(13, 118, 110, 0.4)",
                      border: 'none',
                      height: '32px',
                      textTransform: 'none',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      whiteSpace: 'nowrap',
                      minWidth: isMobile ? '32px' : '100px',
                      width: 'auto',
                      marginLeft: 'auto'
                    }}
                  >
                    <Add style={{ fontSize: '18px' }} />
                    {!isMobile && <span style={{ fontSize: '12px' }}>Create</span>}
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="purchase table">
                <TableHead>
                  <TableRow style={{
                    background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                    height: '28px'
                  }}>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '9px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Bill #
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '9px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Supplier
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '9px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Date
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '9px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Amount
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '9px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Status
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '9px' : '9px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      textAlign: 'right',
                      height: '28px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontSize: isMobile ? '10px' : '0.75rem' }}>
                          No purchases found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row, index) => (
                      <TableRow
                        key={row.Id}
                        sx={{
                          background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          borderBottom: '2px solid #f0f0f0'
                        }}
                      >
                        <TableCell sx={{ py: isMobile ? 0.75 : 1, px: isMobile ? 1 : 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', fontSize: isMobile ? '10px' : '0.75rem' }}>
                            {row.BillNumber || '-'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: isMobile ? '9px' : '0.65rem' }}>
                            {formatDate(row.BillDate)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: isMobile ? 0.75 : 1, px: isMobile ? 1 : 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.3, fontSize: isMobile ? '10px' : '0.75rem' }}>
                            {getSupplierName(row)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: isMobile ? 0.75 : 1, px: isMobile ? 1 : 1.5 }}>
                          <Typography variant="caption" sx={{ display: 'block', fontSize: isMobile ? '9px' : '0.65rem', color: '#6b7280' }}>
                            {formatDate(row.BillDate)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: isMobile ? 0.75 : 1, px: isMobile ? 1 : 1.5 }}>
                          <Typography variant="body2" sx={{ fontSize: isMobile ? '10px' : '0.75rem', fontWeight: 600, color: '#059669' }}>
                            {formatCurrency(row.TotalAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: isMobile ? 0.75 : 1, px: isMobile ? 1 : 1.5 }}>
                          {row.IsActive ? (
                            row.IsVerified ? (
                              <Chip label="Verified" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#8b5cf6 !important', color: '#ffffff !important' }} />
                            ) : row.IsConvertToGoodReceipt ? (
                              <Chip label="Good Receipt" color="success" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#10b981', color: 'white' }} />
                            ) : (
                              <Chip label="Active" color="success" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                            )
                          ) : (
                            <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fee2e2', color: '#dc2626' }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ py: isMobile ? 0.75 : 1, px: isMobile ? 1 : 1.5, textAlign: 'right' }}>
                          <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'flex-end' }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenModal('view', row)}
                                sx={{
                                  color: '#3b82f6',
                                  backgroundColor: '#eff6ff',
                                  width: isMobile ? 24 : 28,
                                  height: isMobile ? 24 : 28,
                                  '&:hover': {
                                    backgroundColor: '#dbeafe',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <Visibility sx={{ fontSize: isMobile ? 12 : 14 }} />
                              </IconButton>
                            </Tooltip>

                            {hasEditMasterPermission && row.IsActive && !row.IsConvertToGoodReceipt && (
                              <Tooltip title="Edit Purchase">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenModal('edit', row)}
                                  sx={{
                                    color: '#f59e0b',
                                    backgroundColor: '#fffbeb',
                                    width: isMobile ? 24 : 28,
                                    height: isMobile ? 24 : 28,
                                    '&:hover': {
                                      backgroundColor: '#fef3c7',
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                >
                                  <Edit sx={{ fontSize: isMobile ? 12 : 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {hasDeleteMasterPermission && row.IsActive && !row.IsVerified && (
                              <Tooltip title="Delete Purchase">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDeleteModal(row.Id, row.BillNumber)}
                                  sx={{
                                    color: '#ef4444',
                                    backgroundColor: '#fef2f2',
                                    width: isMobile ? 24 : 28,
                                    height: isMobile ? 24 : 28,
                                    '&:hover': {
                                      backgroundColor: '#fecaca',
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                >
                                  <Delete sx={{ fontSize: isMobile ? 12 : 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={(newSize) => { setRowsPerPage(newSize); setCurrentPage(1); }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Loading Dialog */}
        {loading && (
          <Dialog open={loading}>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 4 }}>
              <CircularProgress size={48} />
              <Typography>{loadingMessage}</Typography>
            </DialogContent>
          </Dialog>
        )}

        {/* Snackbar Notifications */}
        <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
        </Snackbar>

        {/* Purchase Modal */}
        <PurchaseModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          purchaseDetails={purchaseDetails}
          setPurchaseDetails={setPurchaseDetails}
          onSave={handleSave}
          onVerify={handleVerifyFromModal}
          onToGoodReceipt={handleOpenConvertModal}
          suppliers={suppliers}
          items={items}
          loading={loading}
          freightPackingGST={freightPackingGST}
          companyId={companyId}
          token={token}
          setLoading={setLoading}
          setLoadingMessage={setLoadingMessage}
        />

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#dc2626' }}>
            <Warning /> Delete Purchase
          </DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete purchase <strong>{deleteItemNo}</strong>?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteModal}>Cancel</Button>
            <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Activate Confirmation Modal */}
        <Dialog open={activateModalOpen} onClose={handleCloseActivateModal} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#10b981' }}>
            <CheckCircle /> Activate Purchase
          </DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to activate purchase <strong>{activateItemNo}</strong>?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseActivateModal}>Cancel</Button>
            <Button onClick={handleActivate} variant="contained" color="success">Activate</Button>
          </DialogActions>
        </Dialog>

        {/* Convert to Good Receipt Confirmation Modal */}
        <Dialog open={convertModalOpen} onClose={handleCloseConvertModal} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#10b981' }}>
            <CheckCircle /> Convert to Good Receipt
          </DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to convert purchase <strong>{convertItemNo}</strong> to Good Receipt?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConvertModal}>Cancel</Button>
            <Button onClick={handleToGoodReceipt} variant="contained" color="success">Convert</Button>
          </DialogActions>
        </Dialog>
      </div>
    </Fade>
  );
};

export default PurchaseIndex;
